import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { ModelSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/back-end/store";
import { FormActivity, FormEngineSelectors } from "@com.mgmtp.a12.formengine/formengine-core/lib/client-extensions";
import {
	Document,
	DocumentModel,
	DocumentRtServiceFactory,
	DocumentServiceFactory,
	IGeneratedCodeAccessor
} from "@com.mgmtp.a12.kernel/kernel-md-facade";

import { createCustomNotificationAction } from "../../../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../../localization";
import { validateActivity } from "../../../../utilities/activityUtils";
import { parseImportFile } from "../../../../utilities/rpcHelper";

import { ImportFileAction, importFileActionCreator } from "./actions";

const documentService = new DocumentServiceFactory().getDocumentService();

const ACCEPTED_MIME_TYPES = ["text/xml", "application/xml", "application/json"];

export function* importFileContentSaga(): SagaGenerator<void> {
	yield* takeLatest(importFileActionCreator.match, handle);
}

type EventAction = Action<ImportFileAction>;

function* handle(action: EventAction): SagaGenerator<void> {
	const { importFile, mainActivityId, modalActivityId } = action.payload;

	try {
		if (!isValidFile(importFile)) {
			yield* put(
				createCustomNotificationAction({
					activityId: modalActivityId,
					message: localizableFromLocaleResources(RESOURCE_KEYS.error.FILE_INVALID_MESSAGE_ERROR),
					severity: "error",
					group: "importXmlContentSaga"
				})
			);
			return;
		}

		yield* put(ApplicationActions.setBusy(true));

		// EngineState um später an Form- und DocumentModel heranzukommen
		const engineState = yield* select(FormEngineSelectors.engineState(mainActivityId));
		if (!engineState) {
			throw new Error("Couldn't get EngineState");
		}

		const activity = yield* select(ActivitySelectors.activityById(mainActivityId));
		if (!activity) {
			throw new Error("Couldn't find activity");
		}

		const dataHolder = Activity.findDefaultDataHolder(activity);
		if (dataHolder?.loadingState !== "loaded") {
			throw new Error("Data is not loaded yet!");
		}
		if (!FormActivity.Data.SingleDocumentData.isInstance(dataHolder.data)) {
			throw new Error("Data is not suitable!");
		}

		// DocumentModel, benötigt um korrekt formatierten Request zu erzeugen
		const documentModel = ModelSelectors.documentModel()(engineState);
		const validationCode = ModelSelectors.validationCode()(engineState);

		const fileContent = yield* call(readFileAsText, importFile);

		const document = yield* call(
			parseAndApplyImportFile,
			dataHolder.data.document,
			fileContent,
			documentModel,
			validationCode
		);

		yield* put(ActivityActions.setData({ activityId: mainActivityId, data: { ...dataHolder.data, document } }));

		yield* call(validateActivity, mainActivityId);
		yield* put(ActivityActions.cancel({ activityId: modalActivityId }));
	} catch (e) {
		if (e instanceof Error) {
			yield* put(
				createCustomNotificationAction({
					activityId: modalActivityId,
					message: { key: "noKey", defaults: { de: e.message } },
					severity: "error",
					group: "importXmlContentSaga"
				})
			);
		} else {
			yield* put(
				createCustomNotificationAction({
					activityId: modalActivityId,
					message: localizableFromLocaleResources(RESOURCE_KEYS.error.UNKNOWN_MESSAGE_ERROR),
					severity: "error",
					group: "importXmlContentSaga"
				})
			);
		}
	} finally {
		yield* put(ApplicationActions.setBusy(false));
	}
}

export async function parseAndApplyImportFile(
	document: Activity.Data.Document,
	fileContent: string,
	documentModel: DocumentModel,
	validationCode?: IGeneratedCodeAccessor
): Promise<Activity.Data.Document> {
	const formattedDocument = documentService.formatDates(
		Object.fromEntries(Object.entries(document).filter(([key]) => !["id", "modelId"].includes(key))),
		documentModel
	);

	const updatedDocument = parseImportFile(document.id, formattedDocument, fileContent);
	return updatedDocument
		.then(doc => documentService.parseDates(doc, documentModel) as Document)
		.then(doc => executeComputation(doc, validationCode))
		.then(doc => ({ ...document, ...doc }));
}

function executeComputation(document: Document, validationCode?: IGeneratedCodeAccessor): Document {
	if (!validationCode) {
		return document;
	}

	const documentRtService = DocumentRtServiceFactory.createDocumentRtService(validationCode);
	return documentRtService.applyComputationResult(documentRtService.compute(document), document);
}

function isValidFile(file: File): boolean {
	return file && ACCEPTED_MIME_TYPES.includes(file.type);
}

async function readFileAsText(file: File): Promise<string> {
	return file.text();
}
